/**
 * Derives the spoken narration text for a walkthrough step from the step's own
 * title and HTML body. Phase 1 has no authored narration field, so a step narrates
 * its visible content: the title read first, then the tag-stripped, entity-decoded,
 * whitespace-normalized body.
 *
 * The output is sent to the Sparta walkthrough-audio endpoint AND (via its hash) is
 * the cache key, so it must be deterministic for the same visible content. Kept a
 * pure function so it is unit-tested without a DOM.
 */
export interface WalkthroughNarrationSource {
  title?: string | null;
  html?: string | null;
}

/** Strip HTML tags, decode the common entities, and collapse whitespace to single spaces. */
function stripAndDecodeHtml(html: string): string {
  // Drop <script>/<style> blocks INCLUDING their inner text first: a plain tag-strip would leave
  // that content behind to be read aloud.
  const withoutScripts = html.replace(
    /<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi,
    ' '
  );
  const withoutTags = withoutScripts.replace(/<[^>]*>/g, ' ');
  const decoded = withoutTags
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    // decode &amp; LAST so an encoded entity like &amp;lt; is not double-decoded
    .replace(/&amp;/g, '&');
  return decoded.replace(/\s+/g, ' ').trim();
}

export function deriveWalkthroughNarration(
  step: WalkthroughNarrationSource
): string {
  const title = (step.title ?? '').replace(/\s+/g, ' ').trim();
  const body = stripAndDecodeHtml(step.html ?? '');

  if (!title) {
    return body;
  }
  if (!body) {
    return title;
  }

  // Avoid a doubled terminator when the title already ends a sentence.
  const separator = /[.?!]$/.test(title) ? ' ' : '. ';
  return `${title}${separator}${body}`;
}
